import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type KeyPressCallback, useClickHoldRepeat, useKeyPress } from '@pkg/hooks';
import { assertFloat } from '@pkg/utils';

import {
    type InputNumberChange,
    type InputNumberFocus,
    type InputNumberHookOptions,
    type InputNumberValue,
} from './InputNumber.types';
import { maxWithinBounds, numberFromString, STEP, valueWithinBounds } from './InputNumber.utilities';

export type InputNumberHookReturn = ReturnType<typeof useInputNumber>;

// NOTES:
// 1. Will NOT re-render if `initialValue` changes.
//  - This is by design (treat this like the React `input` API).
// 2. Currently not updating `value` when `min/max/step` changes.
//  - We can achieve this via a `useEffect` if we find the need.
export function useInputNumber({
    initialValue,
    min,
    max,
    step = STEP.DEFAULT,
    disabled = false,
    onFocus,
    onBlur,
    onEnter,
    onEscape,
    onChange,
    onDecrement,
    onIncrement,
    onSubmit,
}: InputNumberHookOptions) {
    const [focused, setFocused] = useState(false);
    const [value, setValue] = useState<InputNumberValue>(initialValue);
    const [queuedValue, setQueuedValue] = useState<InputNumberValue>();

    const prevValueRef = useRef(initialValue);

    const safeMax = useMemo(() => maxWithinBounds({ min, max }), [min, max]);
    const safeStep = useMemo(() => Math.max(STEP.MIN, step), [step]);
    // Currently does not consider decimal values for `min` or `max`.
    const allowFloat = assertFloat(safeStep);

    // TODO: This should probably include `readOnly`.
    const disabledKeyPress = disabled || !focused;
    const disabledDecrement = disabled || (min !== undefined && value !== undefined && value <= min);
    const disabledIncrement = disabled || (safeMax !== undefined && value !== undefined && value >= safeMax);

    const displayValue = focused ? queuedValue : value;
    const pending = focused && queuedValue !== value;

    const handleEnter: KeyPressCallback = () => {
        const calculatedValue =
            queuedValue === undefined ? value : valueWithinBounds(queuedValue, { min, max: safeMax, allowFloat });

        prevValueRef.current = value;
        setValue(calculatedValue);
        setQueuedValue(calculatedValue);

        onEnter?.(calculatedValue);
    };

    const handleEscape: KeyPressCallback = () => {
        setQueuedValue(value);
        onEscape?.(value);
        // TODO: We might want to `ref.current.blur()`.
    };

    useKeyPress(['Enter'], handleEnter, { disabled: disabledKeyPress });
    useKeyPress(['Escape'], handleEscape, { disabled: disabledKeyPress });

    const handleFocus: InputNumberFocus = ({ target }) => {
        const capturedValue = numberFromString(target.value);

        setFocused(true);
        setQueuedValue(capturedValue);
        onFocus?.(capturedValue);
    };

    // TODO: Consider wrapping in `useCallback()` if we find it is needed.
    // useCallback(() => {}, [min, safeMax, value, queuedValue, onBlur, onSubmit]);
    const handleBlur: InputNumberFocus = ({ target }) => {
        setFocused(false);

        // We may want to allow an `undefined` submission...
        // If so, we need to revise this condition.
        if (queuedValue === undefined) {
            // Required in order to undo any unsupported characters:
            target.value = value === undefined ? '' : `${value}`;

            onBlur?.(value);
            return;
        }

        const calculatedValue = valueWithinBounds(queuedValue, { min, max: safeMax, allowFloat });
        prevValueRef.current = value;

        if (queuedValue !== value && value === calculatedValue) {
            // This unique condition is required in order to signal
            // a "revert" back to the "last valid value".
            onSubmit(calculatedValue);
        }

        setValue(calculatedValue);
        setQueuedValue(undefined);
        onBlur?.(calculatedValue);
    };

    const handleChange: InputNumberChange = ({ target }) => {
        const capturedValue = numberFromString(target.value);

        const valueWithFallbacks = capturedValue ?? value ?? min ?? 0;
        // Always passing `allowFloat` for each change as we want
        // to allow real-time errors to me made, and the validation to
        // be reported by the callback. Auto-correction will occur
        // on value "submission".
        const calculatedValue = valueWithinBounds(valueWithFallbacks, { min, max: safeMax, allowFloat: true });

        // Do we want to set a `invalid` state?
        const floatViolation = assertFloat(capturedValue) && !allowFloat;
        const valid = capturedValue === calculatedValue && !floatViolation;

        setQueuedValue(capturedValue);
        onChange?.(capturedValue, valid, calculatedValue);
    };

    const handleDecrementOnce = useCallback(() => {
        let outsideValue = value;

        setValue((current) => {
            const resolvedValue = current ?? Math.max(0, min ?? 0);
            const decrementedValue = resolvedValue - safeStep;
            const calculatedValue = valueWithinBounds(decrementedValue, { min, max: safeMax, allowFloat });

            prevValueRef.current = current;
            outsideValue = calculatedValue;

            return calculatedValue;
        });

        onDecrement?.(outsideValue);
    }, [min, safeMax, safeStep, value, allowFloat, onDecrement]);

    const {
        active: decrementing,
        handleAction: handleDecrementRepeat,
        handleReset: handleDecrementRepeatReset,
    } = useClickHoldRepeat(handleDecrementOnce, {
        disabled: disabledDecrement,
        leftPointerOnly: true,
        // Skipping as we expect `onAction` to also be used.
        skipImmediateExecution: true,
    });

    const handleIncrementOnce = useCallback(() => {
        let outsideValue = value;

        setValue((current) => {
            const resolvedValue = current ?? Math.min(0, safeMax ?? 0);
            const incrementedValue = resolvedValue + safeStep;
            const calculatedValue = valueWithinBounds(incrementedValue, { min, max: safeMax, allowFloat });

            prevValueRef.current = current;
            outsideValue = calculatedValue;

            return calculatedValue;
        });

        onIncrement?.(outsideValue);
    }, [min, safeMax, safeStep, value, allowFloat, onIncrement]);

    const {
        active: incrementing,
        handleAction: handleIncrementRepeat,
        handleReset: handleIncrementRepeatReset,
    } = useClickHoldRepeat(handleIncrementOnce, {
        disabled: disabledIncrement,
        leftPointerOnly: true,
        // Skipping as we expect `onAction` to also be used.
        skipImmediateExecution: true,
    });

    // We are intentionally keeping `onSubmit` out of the
    // dependency list, as we don't want to re-submit
    // simply because the callback has changed.
    useEffect(() => {
        if (value !== prevValueRef.current) onSubmit(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return {
        value,
        queuedValue,
        displayValue,
        safeMax,
        safeStep,
        disabledKeyPress,
        disabledDecrement,
        disabledIncrement,
        pending,
        focused,
        handleFocus,
        handleBlur,
        handleChange,
        decrementing,
        handleDecrementOnce,
        handleDecrementRepeat,
        handleDecrementRepeatReset,
        incrementing,
        handleIncrementOnce,
        handleIncrementRepeat,
        handleIncrementRepeatReset,
    };
}
